import {
  Badge,
  Box,
  ChatBubble,
  ChatMessage,
  ChatMessageMeta,
  ChatMessageMetaItem,
  Separator,
} from "@twilio-paste/core";
import {
  ReduxMedia,
  ReduxMessage,
} from "../../store/reducers/messageListReducer";
import React, { ReactNode, useState } from "react";
import MessageEditMode from "./MessageEditMode";
import MessageMedia from "./MessageMedia";
import { CONVERSATION_MESSAGES, MAX_MESSAGE_LINE_WIDTH } from "../../constants";
import wrap from "word-wrap";
import { getSdkMessageObject } from "../../conversations-objects";
import Reactions from "./Reactions";
import MenuMessageButton from "./MenuMessageButton";
import { MessageStatus } from "./MessageStatus";
import { getMessageTime } from "../../utils/timestampUtils";
import { ReduxParticipant } from "../../store/reducers/participantsReducer";
import {
  successNotification,
  unexpectedErrorNotification,
} from "../../helpers";
import { NotificationsType } from "../../store/reducers/notificationsReducer";
import { ReactionsType } from "../../types";

const today = new Date().toDateString();

interface MessageItemProps {
  message: ReduxMessage;
  conversationAttachments: Record<string, Record<string, Blob>>;
  onDownloadAttachments: (message: ReduxMessage) => Promise<Error | undefined>;
  onFileOpen: (file: Blob, { filename }: ReduxMedia) => void;
  setImagePreview: (imagePreview: {
    message: ReduxMessage;
    file: Blob;
    sid: string;
  }) => void;
  getAuthorFriendlyName: (message: ReduxMessage) => string;
  participants: ReduxParticipant[];
  use24hTimeFormat: boolean;
  firstMessagePerDay: string[];
  addNotifications: (notification: NotificationsType) => void;
  isMine: boolean;
}

const MessageItem: React.FC<MessageItemProps> = (props: MessageItemProps) => {
  const {
    message,
    conversationAttachments,
    onDownloadAttachments,
    onFileOpen,
    setImagePreview,
    getAuthorFriendlyName,
    participants,
    use24hTimeFormat,
    firstMessagePerDay,
    addNotifications,
    isMine,
  } = props;
  const [isEditingMessage, setIsEditingMessage] = useState(false);
  const messageImages: ReduxMedia[] = [];
  const messageFiles: ReduxMedia[] = [];
  const currentDateCreated = message.dateCreated ?? null;
  (message.attachedMedia || []).forEach((file) => {
    const { contentType } = file;
    if (contentType.includes("image")) {
      messageImages.push(file);
      return;
    }
    messageFiles.push(file);
  });
  const attributes = message.attributes as Record<
    string,
    ReactionsType | undefined
  >;

  const wrappedBody = wrap(message.body ?? "", {
    width: MAX_MESSAGE_LINE_WIDTH,
    indent: "",
    cut: true,
  });

  const isOutbound = isMine;

  const MetaItemWithMargin: React.FC<{ children: ReactNode }> = (props) => (
    <ChatMessageMetaItem>
      <div style={{ marginTop: "5px" }}>{props.children}</div>
    </ChatMessageMetaItem>
  );

  const handleEditMessage = async (
    message: ReduxMessage,
    editedText: string
  ) => {
    try {
      await getSdkMessageObject(message).updateBody(editedText);
      message.body = editedText;
      successNotification({
        message: `${CONVERSATION_MESSAGES.MESSAGE_EDITED}`,
        addNotifications,
      });
      setIsEditingMessage(false);
    } catch (e) {
      unexpectedErrorNotification(
        `${CONVERSATION_MESSAGES.MESSAGE_EDIT_FAILED} ${e.message}`,
        addNotifications
      );
    }
  };

  let metaItems = [
    <ChatMessageMetaItem key={`${message.sid}_margin_0`}>
      <Reactions
        showAddReactionButton={!isOutbound}
        reactions={attributes.reactions}
        onReactionsChanged={(reactions) => {
          getSdkMessageObject(message).updateAttributes({
            ...attributes,
            reactions,
          });
        }}
      />
      {isOutbound && (
        <MenuMessageButton
          menuElements={[
            {
              id: "reactions",
              label: "",
              enabled: true,
              hideOnClick: false,
              customComponent: (handleCloseMenu) => (
                <Reactions
                  callback={handleCloseMenu as () => void}
                  showAsLabel
                  showReactionsCount={false}
                  reactions={attributes.reactions}
                  onReactionsChanged={(reactions) => {
                    getSdkMessageObject(message).updateAttributes({
                      ...attributes,
                      reactions,
                    });
                  }}
                />
              ),
            },
          ]}
        />
      )}
    </ChatMessageMetaItem>,
    <MetaItemWithMargin key={`${message.sid}_margin_1`}>
      <MessageStatus message={message} channelParticipants={participants} />
    </MetaItemWithMargin>,
    <MetaItemWithMargin key={`${message.sid}_margin_2`}>
      {isOutbound
        ? `${getAuthorFriendlyName(message)}・${getMessageTime(
            message,
            use24hTimeFormat
          )}`
        : `${getMessageTime(
            message,
            use24hTimeFormat
          )}・${getAuthorFriendlyName(message)}`}
    </MetaItemWithMargin>,
  ];

  if (isOutbound) {
    metaItems = metaItems.reverse();
  }

  return (
    <div>
      {currentDateCreated && firstMessagePerDay.includes(message.sid) && (
        <>
          <Separator orientation="horizontal" verticalSpacing="space50" />
          <Box
            display="flex"
            flexWrap="wrap"
            justifyContent="center"
            alignItems="center"
          >
            <Badge as="span" variant="neutral">
              {currentDateCreated.toDateString() === today
                ? "Today"
                : currentDateCreated.toDateString()}
            </Badge>
          </Box>
        </>
      )}
      <ChatMessage
        variant={isOutbound ? "outbound" : "inbound"}
        key={`${message.sid}.message`}
      >
        {isEditingMessage ? (
          <MessageEditMode
            message={message}
            cancelEdit={() => setIsEditingMessage(false)}
            editAction={handleEditMessage}
            variant={isOutbound ? "outbound" : "inbound"}
          />
        ) : (
          <ChatBubble>
            {wrappedBody}
            <MessageMedia
              key={message.sid}
              attachments={conversationAttachments?.[message.sid]}
              onDownload={async () => await onDownloadAttachments(message)}
              images={messageImages}
              files={messageFiles}
              sending={message.index === -1}
              onOpen={(
                mediaSid: string,
                image?: ReduxMedia,
                file?: ReduxMedia
              ) => {
                if (file) {
                  onFileOpen(
                    conversationAttachments?.[message.sid][mediaSid],
                    file
                  );
                  return;
                }
                if (image) {
                  setImagePreview({
                    message,
                    file: conversationAttachments?.[message.sid][mediaSid],
                    sid: mediaSid,
                  });
                }
              }}
            />
          </ChatBubble>
        )}
        <ChatMessageMeta
          aria-label={`said by ${getAuthorFriendlyName(message)}`}
        >
          {metaItems.map((item) => {
            return item;
          })}
        </ChatMessageMeta>
      </ChatMessage>
    </div>
  );
};

export default MessageItem;
